import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { milestones } from "@/mocks/story";

export default function Timeline() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".timeline-item", {
        y: 60,
        opacity: 0,
        duration: 1,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: { trigger: root.current, start: "top 70%" },
      });

      gsap.from(".timeline-line", {
        scaleY: 0,
        transformOrigin: "top",
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top 60%",
          end: "bottom 60%",
          scrub: 0.6,
        },
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={root} className="bg-forest text-bone py-24 md:py-36 px-6 md:px-10 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(245,241,234,0.6) 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-5xl mx-auto">
        <div className="text-center mb-16 md:mb-24">
          <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-clay mb-4">
            ✦ The journey so far
          </p>
          <h2 className="font-display text-5xl md:text-7xl tracking-tightest font-light leading-[0.95]">
            Milestones, <span className="font-display text-clay">not deadlines.</span>
          </h2>
        </div>

        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-bone/15 hidden md:block">
            <div className="timeline-line absolute inset-0 bg-clay"></div>
          </div>

          <div className="space-y-16 md:space-y-24">
            {milestones.map((m, i) => (
              <div
                key={m.year}
                className={`timeline-item relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center ${
                  i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="md:px-12">
                  <span className="font-mono text-xs tracking-[0.3em] text-clay">{m.year}</span>
                  <h3 className="mt-3 font-display text-4xl md:text-5xl tracking-tightest font-light text-bone leading-none">
                    {m.title}
                  </h3>
                  <p className="mt-5 text-bone/70 text-base md:text-lg leading-relaxed max-w-md">
                    {m.body}
                  </p>
                </div>
                <div className="md:px-12">
                  <div className="hidden md:flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-clay/20 flex items-center justify-center">
                      <span className="font-mono text-sm text-clay font-bold">{m.year.slice(-2)}</span>
                    </div>
                    <div className="h-px flex-1 bg-bone/10"></div>
                  </div>
                </div>
                {/* Dot on the line */}
                <div className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-clay z-10 ring-4 ring-forest"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}